import { IconButton, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  addBookmarksBulk,
  bookmarkBulkLimit,
  bulkArchiveDatasets,
  datasetBulkArchiveLimit,
  datasetBulkMoveLimit,
  moveDatasetsToFolder,
  type DatasetFolderTree,
  datasetMergeSourceMax,
  datasetMergeSourceMin,
  enqueueDatasetMerge,
  type DatasetMergeJobResp,
} from "@/api";
import { canMergeDatasets, canModifyDatasetFolder, type UserRole } from "@/app";
import { resolveErrorMessage } from "@/components/dataset";
import { FolderSelectField, noFolderValue } from "@/components/dataset/folder";
import { HatTextModal } from "@/components/modal";
import { isHttpClientError } from "@/http";
import type { DatasetTableRow } from "../DatasetTableRow";
import { DatasetMergeDialog, type DatasetMergeTarget } from "./DatasetMergeDialog";
import { supportsStorageFeature } from "@/api/datasetStorageBackend";
import {
  storageBackendErrorText,
  storageFeatureUnsupportedText,
} from "@/components/dataset/storageBackendText";

export type DatasetSelectionActionsProps = {
  // 현재 선택된 행. 선택은 source 가 소유하므로 페이지를 넘겨도 유지된다.
  selected: readonly DatasetTableRow[];
  currentUserRole: UserRole | null;
  // 통합 잡이 이미 실행 중이면 true 다. 트리거 버튼을 잠근다(진행 관측은 DatasetMergeJobChip 이 소유한다).
  mergeActive: boolean;
  // 통합이 큐잉(202)되면 잡을 전달한다. 페이지가 진행 칩을 footer 에 표시한다.
  onMergeStarted: (job: DatasetMergeJobResp) => void;
  // 액션이 끝난 뒤 정리한다. listChanged 면 목록도 재조회한다(북마크는 목록을 바꾸지 않는다).
  onCompleted: (result: { listChanged: boolean }) => void;
  // 폴더 트리. 이동 대상 목록의 출처다. 아직 받지 못했으면 null 이고, 그때 이동 버튼을 잠근다.
  folderTree: DatasetFolderTree | null;
  // 폴더 이동이 끝났다. 폴더별 건수가 바뀌므로 트리를 다시 받는다.
  onFoldersChanged: () => void;
};

/**
 * Dataset 목록의 선택 액션. 데이터셋 통합, 북마크, 보관, 폴더 이동이다. 보관은 선택한 행이
 * 모두 내 것(`can_write`)일 때만 가능하다. 서버는 하나라도 다른 사람의 것이면 전체를 403 으로
 * 거절한다.
 *
 * 표 상단의 BulkActionBar(overlay) 안에 둔다. 개수, 전체선택, 해제는 바가 소유하고, 여기에는
 * 액션 버튼과 그 다이얼로그만 둔다. 선택이 없으면 바와 함께 사라진다.
 *
 * 통합은 큐잉(202)한 뒤 잡을 페이지에 전달하고, footer 진행 칩이 관측한다. 북마크와 보관은 선택
 * 전체를 한 요청으로 보낸다(항목당 호출 금지, `docs/rules.md` §7). 상한(BE 검증과 같은 값)을
 * 넘긴 선택은 버튼을 잠근다.
 */
export function DatasetSelectionActions({
  selected,
  currentUserRole,
  mergeActive,
  onMergeStarted,
  onCompleted,
  folderTree,
  onFoldersChanged,
}: DatasetSelectionActionsProps): ReactNode {
  const [busy, setBusy] = useState(false);
  const [mergeTargets, setMergeTargets] = useState<readonly DatasetMergeTarget[] | null>(null);
  const [mergeErrorMessage, setMergeErrorMessage] = useState<string | null>(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(noFolderValue);
  const [moveErrorMessage, setMoveErrorMessage] = useState<string | null>(null);

  // 언마운트 뒤 도착한 응답이 상태를 바꾸지 않게 하는 가드. 토스트는 결과 통지라 그대로 표시한다.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const canMerge = canMergeDatasets(currentUserRole);
  // 선택은 페이지를 넘어 유지되므로 행 스냅샷이 오래될 수 있다. 그때 오는 403 은 오류 문구가 처리한다.
  const canArchiveSelection = selected.every((row) => row.can_write);
  const canMoveToFolder = canModifyDatasetFolder(currentUserRole);
  const count = selected.length;
  // 통합을 지원하지 않는 저장 위치의 Dataset 이 선택에 하나라도 있으면 요청 전체가 409 다.
  const mergeUnsupported = selected.find(
    (row) => !supportsStorageFeature(row.storage_backend, "merge"),
  );
  const mergeBlockedReason =
    mergeUnsupported === undefined
      ? mergeDisabledReason(count)
      : `${storageFeatureUnsupportedText(mergeUnsupported.storage_backend, "merge")} 선택에서 빼 주세요.`;
  const uuids = selected.map((row) => row.uuid);

  const handleMergeSubmit = (displayName: string): void => {
    if (busy || mergeTargets === null) {
      return;
    }
    setBusy(true);
    setMergeErrorMessage(null);
    void enqueueDatasetMerge(
      {
        source_dataset_uuids: mergeTargets.map((target) => target.uuid),
        display_name: displayName,
      },
      { role: currentUserRole },
    )
      .then((started) => {
        toast.success("통합을 시작했습니다. 끝나면 알려드립니다.");
        onMergeStarted(started);
        if (aliveRef.current) {
          setBusy(false);
          setMergeTargets(null);
        }
      })
      .catch((error: unknown) => {
        if (aliveRef.current) {
          setBusy(false);
          setMergeErrorMessage(resolveMergeErrorMessage(error));
        }
      });
  };

  const handleBookmarkPress = (): void => {
    if (busy || count === 0) {
      return;
    }
    setBusy(true);
    void addBookmarksBulk(uuids)
      .then((result) => {
        toast.success(bookmarkResultText(result.added, result.already_present));
        if (aliveRef.current) {
          setBusy(false);
        }
        onCompleted({ listChanged: false });
      })
      .catch((error: unknown) => {
        toast.error(resolveErrorMessage(error));
        if (aliveRef.current) {
          setBusy(false);
        }
      });
  };

  const handleArchiveConfirm = (): void => {
    if (busy || count === 0) {
      return;
    }
    setBusy(true);
    void bulkArchiveDatasets(uuids)
      .then((result) => {
        if (result.skipped.length > 0) {
          toast.error(archiveSkippedText(result.applied, result.skipped.length));
        } else {
          toast.success(`Dataset ${result.applied}개를 보관했습니다.`);
        }
        if (aliveRef.current) {
          setBusy(false);
          setArchiveConfirmOpen(false);
        }
        onCompleted({ listChanged: true });
      })
      .catch((error: unknown) => {
        toast.error(resolveErrorMessage(error));
        if (aliveRef.current) {
          setBusy(false);
          setArchiveConfirmOpen(false);
        }
      });
  };

  const handleMoveConfirm = (): void => {
    if (busy || count === 0) {
      return;
    }
    setBusy(true);
    setMoveErrorMessage(null);
    void moveDatasetsToFolder(
      {
        dataset_uuids: uuids,
        folder_uuid: moveTarget === noFolderValue ? null : moveTarget,
      },
      { role: currentUserRole },
    )
      .then((result) => {
        // `applied` 는 중복 제거 후 대상 수다. 이미 그 폴더에 있던 항목도 요청한 상태가
        // 되었으므로 성공으로 센다(건너뛰는 경우가 없어 부분 처리 문구가 없다).
        toast.success(`Dataset ${result.applied}개를 옮겼습니다.`);
        if (aliveRef.current) {
          setBusy(false);
          setMoveOpen(false);
        }
        onFoldersChanged();
        onCompleted({ listChanged: true });
      })
      .catch((error: unknown) => {
        if (aliveRef.current) {
          setBusy(false);
          setMoveErrorMessage(resolveErrorMessage(error));
        }
      });
  };

  return (
    <>
      {canMerge ? (
        <IconButton
          text="Dataset 통합"
          type="button"
          size="sm"
          variant="surface"
          disabled={busy || mergeActive || mergeBlockedReason !== null}
          title={
            mergeActive
              ? "통합이 진행 중입니다. 끝난 뒤 다시 시도해 주세요."
              : (mergeBlockedReason ?? "선택한 Dataset을 하나로 통합합니다.")
          }
          onClick={() => {
            setMergeErrorMessage(null);
            setMergeTargets(toMergeTargets(selected));
          }}
        />
      ) : null}
      <IconButton
        text="북마크"
        type="button"
        size="sm"
        variant="surface"
        disabled={busy || count === 0 || count > bookmarkBulkLimit}
        title={
          count > bookmarkBulkLimit
            ? `한 번에 ${bookmarkBulkLimit}개까지 담을 수 있습니다.`
            : "선택한 Dataset을 북마크에 담습니다."
        }
        onClick={handleBookmarkPress}
      />
      <IconButton
        text="보관"
        type="button"
        size="sm"
        variant="surface"
        disabled={busy || count === 0 || count > datasetBulkArchiveLimit || !canArchiveSelection}
        title={
          !canArchiveSelection
            ? "내가 만든 Dataset만 보관할 수 있습니다(다른 사람의 Dataset이 선택에 섞여 있습니다)."
            : count > datasetBulkArchiveLimit
              ? `한 번에 ${datasetBulkArchiveLimit}개까지 보관할 수 있습니다.`
              : "선택한 Dataset을 보관됨으로 옮깁니다."
        }
        onClick={() => setArchiveConfirmOpen(true)}
      />
      {canMoveToFolder ? (
        <IconButton
          text="폴더로 이동"
          type="button"
          size="sm"
          variant="surface"
          disabled={busy || count === 0 || count > datasetBulkMoveLimit || folderTree === null}
          title={
            count > datasetBulkMoveLimit
              ? `한 번에 ${datasetBulkMoveLimit}개까지 옮길 수 있습니다.`
              : "선택한 Dataset을 폴더로 옮깁니다."
          }
          onClick={() => {
            setMoveTarget(noFolderValue);
            setMoveErrorMessage(null);
            setMoveOpen(true);
          }}
        />
      ) : null}
      {moveOpen && folderTree !== null ? (
        <HatTextModal
          open
          label={`선택한 Dataset ${count}개를 옮깁니다.`}
          caption="폴더는 조직이 함께 보는 정리 축입니다. Dataset 하나는 폴더 하나에 속합니다."
          okText="옮기기"
          busy={busy}
          errorMessage={moveErrorMessage ?? undefined}
          onOkPress={handleMoveConfirm}
          onCancelPress={() => {
            if (!busy) {
              setMoveOpen(false);
            }
          }}
          content={
            <FolderSelectField
              tree={folderTree}
              label="옮길 폴더"
              value={moveTarget}
              onValueChange={setMoveTarget}
              noFolderLabel="미분류로 꺼내기"
              disabled={busy}
            />
          }
        />
      ) : null}
      {mergeTargets !== null ? (
        <DatasetMergeDialog
          targets={mergeTargets}
          submitting={busy}
          errorMessage={mergeErrorMessage}
          onClose={() => {
            if (!busy) {
              setMergeTargets(null);
            }
          }}
          onSubmit={handleMergeSubmit}
        />
      ) : null}
      <HatTextModal
        open={archiveConfirmOpen}
        label={`선택한 Dataset ${count}개를 보관합니다.`}
        caption="보관하면 기본 목록에서 숨고 보관됨 목록으로 옮겨집니다. Training·북마크가 참조하는 데이터는 그대로 남고, 복원은 Dataset 상세에서 할 수 있습니다."
        okText="보관"
        error
        busy={busy}
        onOkPress={handleArchiveConfirm}
        onCancelPress={() => setArchiveConfirmOpen(false)}
      />
    </>
  );
}

function toMergeTargets(rows: readonly DatasetTableRow[]): readonly DatasetMergeTarget[] {
  return rows.map((row) => ({
    uuid: row.uuid,
    displayName: row.display_name,
    totalEpisodes: row.total_episodes,
  }));
}

/** 통합 버튼이 잠긴 이유. 잠기지 않았으면 null. */
function mergeDisabledReason(count: number): string | null {
  if (count < datasetMergeSourceMin) {
    return `통합은 Dataset ${datasetMergeSourceMin}개 이상을 선택해야 합니다.`;
  }
  if (count > datasetMergeSourceMax) {
    return `한 번에 ${datasetMergeSourceMax}개까지 통합할 수 있습니다.`;
  }
  return null;
}

/** 담기 결과 통지. 이미 담겨 있던 항목은 실패가 아니라 현재 상태로 안내한다. */
function bookmarkResultText(added: number, alreadyPresent: number): string {
  if (alreadyPresent === 0) {
    return `Dataset ${added}개를 북마크에 담았습니다.`;
  }
  if (added === 0) {
    return `선택한 ${alreadyPresent}개는 이미 북마크에 담겨 있습니다.`;
  }
  return `Dataset ${added}개를 담았습니다. ${alreadyPresent}개는 이미 담겨 있었습니다.`;
}

/** 부분 처리 통지. 남은 이유와 다음 행동을 함께 안내한다. */
function archiveSkippedText(applied: number, skipped: number): string {
  return `Dataset ${applied}개를 보관했습니다. ${skipped}개는 상태가 바뀌어 보관하지 못했습니다. 목록을 새로 불러온 뒤 다시 시도해 주세요.`;
}

/**
 * 트리거 실패 문구. 계약된 403/404/409/422 는 원인을 구체적으로 안내한다.
 *
 * `409` 는 서버 문장이 사용자용 한국어라 그대로 쓴다. `422` 는 영문 기술 문장이라
 * 코드로 구분해 문구를 만든다.
 */
function resolveMergeErrorMessage(error: unknown): string {
  // 저장 위치 오류의 서버 문장은 영어다. 아래의 일반 409 분기보다 먼저 가른다.
  const storageBackendText = storageBackendErrorText(error);
  if (storageBackendText !== null) {
    return storageBackendText;
  }
  if (isHttpClientError(error)) {
    if (error.status === 403) {
      return "Dataset 통합 권한이 없습니다.";
    }
    if (error.status === 404) {
      return "선택한 Dataset 중 찾을 수 없는 항목이 있습니다. 목록을 새로 불러와 주세요.";
    }
    if (error.status === 409) {
      return error.message;
    }
    if (error.status === 422) {
      return mergeUnprocessableText(error.code);
    }
  }
  return resolveErrorMessage(error);
}

/** 422 사유 코드 → 문구. 모르는 코드는 서버 영문 대신 일반 안내로 표시한다. */
function mergeUnprocessableText(code: string | null): string {
  switch (code) {
    case "dataset.merge.source_not_available":
      return "보관됐거나 아직 준비 중인 Dataset은 통합할 수 없습니다. 사용 가능한 Dataset만 선택해 주세요.";
    case "dataset.merge.too_few_sources":
      return `서로 다른 Dataset을 ${datasetMergeSourceMin}개 이상 선택해 주세요.`;
    default:
      return "선택한 Dataset으로는 통합할 수 없습니다. 선택을 다시 확인해 주세요.";
  }
}
